import React, { useState, useCallback, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { debounce } from '../utils/helpers';
import { useI18n } from '../i18n/index.jsx';
// Using regular CSS classes instead of CSS modules for shadow DOM compatibility

const SearchBox = ({ placeholder = null, value: initialValue, onChange, ...props }) => {
  const { t } = useI18n();
  // Internal state for immediate display updates - initialize with prop value
  const [internalValue, setInternalValue] = useState('');

  useEffect(() => {
    setInternalValue(initialValue || '');
  }, [initialValue]);

  // Create debounced version of onChange function
  const debouncedOnChange = useCallback(
    debounce(searchValue => {
      if (onChange) {
        onChange(searchValue);
      }
    }, 300),
    [onChange]
  );

  const handleKeyUp = e => {
    if (e.key === 'Enter') {
      // Immediate execution on Enter key
      if (onChange) {
        onChange(e.target.value);
      }
    }
  };

  const handleBlur = e => {
    // Immediate execution on blur
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const handleChange = e => {
    const newValue = e.target.value;
    // Update internal state immediately for user feedback
    setInternalValue(newValue);
    // Use debounced function for search propagation
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    // Clear both internal state and propagate immediately
    setInternalValue('');
    if (onChange) {
      onChange('');
    }
  };

  const hasValue = internalValue && internalValue.length > 0;

  return (
    <div className="search-box">
      <input
        id="searchQueryInput"
        type="text"
        placeholder={placeholder || t('search_placeholder_generic')}
        value={internalValue}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        className="search-box__input"
        {...props}
      />
      {hasValue && (
        <X
          aria-label={t('clear_search')}
          className="search-box__clear"
          size={20}
          color="#868686"
          onClick={handleClear}
          style={{ cursor: 'pointer' }}
        />
      )}
      <Search className="search-box__icon" size={20} color="#868686" />
    </div>
  );
};

export default SearchBox;
